import {
  ClickAndCollectSortBy,
  ClickAndCollectStore,
  ClickAndCollectStoreProduct,
  Cookie,
  LatLngLocation,
  StoreInformation,
} from '@as-react/common-types';
import { QueryApiOptions, UrlMapping } from '../../types/types';
import { AxiosRequestConfig } from 'axios';
import { axiosClient } from '@as-react/common-utils';
import { createUrl } from '../../utils/createUrl';
import { storeQueryKeys } from './store.queries';
import { CookieUtil, sortStoresBy } from '@as-react/common-utils';

export type StoreInformationParams = {
  businessUnitId: string;
  storeId: number;
  weeks?: number;
};

export type ClickAndCollectStoresOpeningsHoursParams = {
  businessUnitId: string;
  fictiveShopId: string;
  productData: Array<ClickAndCollectStoreProduct>;
  location: LatLngLocation;
  onlyInStock: boolean;
  onlyInSpecialtyStore: boolean;
  sortBy: ClickAndCollectSortBy;
  isShopLanding?: boolean;
  selectedStoreId?: number;
};

export type ClickAndCollectStoresParams = {
  location: LatLngLocation;
  inStore: boolean;
  allowMultiFascia: boolean;
  sortBy: ClickAndCollectSortBy;
  selectedStoreId?: number;
};

const urlMapping: UrlMapping<typeof storeQueryKeys> = {
  getStoreInformation: {
    client: 'api/store/schedule',
    // server: '/configuration-service/store/openinghours',
  },
  getClickAndCollectStoresOpeningsHours: {
    client: 'api/basket/store/openingshours',
    // server: '/clickandcollect-promise-service/clickandcollect/bu/enriched/openinghours'
  },
  getClickAndCollectStores: {
    client: 'api/store/clickandcollect/',
    // server: '/configuration-service/store/clickandcollect/',
  },
};

export const getStoreInformation = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { businessUnitId, storeId, weeks = 1 }: StoreInformationParams
): Promise<StoreInformation> => {
  const url = createUrl(servicesEndpoint, urlMapping.getStoreInformation, isServer);
  const { market, language, shopId } = defaultRequestParams;
  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      market,
      language,
      businessUnitId,
      fictiveWebShopId: shopId,
      storeId,
      weeks,
    },
  };

  const request = await axiosClient.request(axiosConfig);
  return request.data;
};

export const getClickAndCollectStoresOpeningsHours = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  {
    businessUnitId,
    fictiveShopId,
    productData,
    location,
    onlyInStock,
    onlyInSpecialtyStore,
    sortBy,
    isShopLanding = false,
    selectedStoreId,
  }: ClickAndCollectStoresOpeningsHoursParams
): Promise<Array<ClickAndCollectStore>> => {
  const url = createUrl(servicesEndpoint, urlMapping.getClickAndCollectStoresOpeningsHours, isServer);

  const axiosConfig: AxiosRequestConfig = {
    method: 'POST',
    url,
    data: {
      market: defaultRequestParams.market,
      businessUnitId,
      mainWebShop: defaultRequestParams.mainWebshop,
      fictiveShopId,
      language: defaultRequestParams.language,
      latitude: location?.lat,
      longitude: location?.lng,
      onlyInStock,
      onlyInSpecialtyStore,
      products: productData,
    },
  };

  const request = await axiosClient.request(axiosConfig);

  if (request?.data?.length > 0) {
    const allStores: Array<ClickAndCollectStore> = request.data.filter(
      (store: ClickAndCollectStore) => store.longitude && store.latitude
    );
    const firstStore = allStores[0]!;
    let stores = allStores;
    let sortedStores = stores;

    if (isShopLanding) {
      stores = allStores.slice(1);
    }

    stores = sortStoresBy(stores, sortBy, selectedStoreId);

    if (isShopLanding) {
      sortedStores = [firstStore, ...stores];
    }

    return sortedStores;
  }

  return request.data;
};

export const getClickAndCollectStores = async (
  { servicesEndpoint, isServer, defaultRequestParams }: QueryApiOptions,
  { location, inStore, allowMultiFascia, sortBy, selectedStoreId }: ClickAndCollectStoresParams
): Promise<Array<ClickAndCollectStore>> => {
  const cookie = CookieUtil.get(Cookie.CLICK_AND_COLLECT_STORE);
  const url = createUrl(servicesEndpoint, urlMapping.getClickAndCollectStores, isServer, undefined, false);

  const axiosConfig: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: {
      market: defaultRequestParams.market,
      mainWebShopName: defaultRequestParams.mainWebshop,
      webShopId: defaultRequestParams.shopId,
      allowMultiFasciaClickAndCollect: allowMultiFascia,
      language: defaultRequestParams.language,
      latitude: location.lat,
      longitude: location.lng,
      inStore,
    },
  };

  if (cookie) {
    axiosConfig.params.storeId = cookie;
  }

  const request = await axiosClient.request(axiosConfig);

  if (request?.data?.length > 0) {
    const allStores: Array<ClickAndCollectStore> = request.data.filter(
      (store: ClickAndCollectStore) => store.longitude && store.latitude
    );

    return sortStoresBy(allStores, sortBy, selectedStoreId);
  }

  return request.data;
};
